// LT-311 — Templates sub-route. Combines Email + SMS templates behind a
// single tab. A local toggle switches between the two; the `?type=sms`
// query param is respected so `/email-templates` → /settings/templates
// and `/sms-templates` → /settings/templates?type=sms both land in the
// right view. SMS section stays gated by the user's canSms entitlement —
// non-SMS users only see the Email toggle + content.
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import EmailTemplatesPage from '../EmailTemplatesPage/EmailTemplatesPage';
import SMSTemplatesPage from '../SMSTemplatesPage/SMSTemplatesPage';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';

type TemplateKind = 'email' | 'sms';

const SettingsTemplatesTab: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);

  const rawType = params.get('type');
  const kind: TemplateKind = rawType === 'sms' && canSms ? 'sms' : 'email';

  const setKind = (next: TemplateKind) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'email') {
      nextParams.delete('type');
    } else {
      nextParams.set('type', next);
    }
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Template kind"
        className="inline-flex rounded-ds-lg border border-white/[0.08] bg-surface-1 p-1"
      >
        <button
          role="tab"
          aria-selected={kind === 'email'}
          onClick={() => setKind('email')}
          className={`px-3 py-1 text-sm rounded-ds-md transition-colors ${
            kind === 'email'
              ? 'bg-accent-tint text-accent-text border border-accent-border'
              : 'text-slate-400 hover:text-slate-200 border border-transparent'
          }`}
        >
          Email
        </button>
        {canSms ? (
          <button
            role="tab"
            aria-selected={kind === 'sms'}
            onClick={() => setKind('sms')}
            className={`px-3 py-1 text-sm rounded-ds-md transition-colors ${
              kind === 'sms'
                ? 'bg-accent-tint text-accent-text border border-accent-border'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}
          >
            SMS
          </button>
        ) : null}
      </div>

      {kind === 'email' ? <EmailTemplatesPage /> : <SMSTemplatesPage />}
    </div>
  );
};

export default SettingsTemplatesTab;
